import React, { useState, useEffect } from 'react';
import { FaUser, FaCalendarAlt, FaAddressBook, FaSignOutAlt, FaSearch, FaFileCsv, FaEdit, FaTrash } from 'react-icons/fa';

const sections = [
  { key: 'contacts', label: 'Contacts', icon: <FaAddressBook /> },
  { key: 'appointments', label: 'Appointments', icon: <FaCalendarAlt /> },
  { key: 'users', label: 'Users', icon: <FaUser /> },
];

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('edit'); // 'edit' or 'delete'
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    setContacts(JSON.parse(localStorage.getItem('contactSubmissions') || '[]'));
    setAppointments(JSON.parse(localStorage.getItem('appointments') || '[]'));
    setUsers(JSON.parse(localStorage.getItem('users') || '[]'));
  }, []);

  // Filtered data
  const getFilteredData = () => {
    if (activeSection === 'contacts') {
      return contacts.filter(c => JSON.stringify(c).toLowerCase().includes(search.toLowerCase()));
    } else if (activeSection === 'appointments') {
      return appointments.filter(a => JSON.stringify(a).toLowerCase().includes(search.toLowerCase()));
    } else if (activeSection === 'users') {
      return users.filter(u => JSON.stringify(u).toLowerCase().includes(search.toLowerCase()));
    }
    return [];
  };

  // Export CSV (basic implementation)
  const handleExport = () => {
    const data = getFilteredData();
    if (!data.length) return;
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeSection}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Open edit modal
  const handleEdit = (item) => {
    setSelectedItem(item);
    setEditData({ ...item });
    setModalType('edit');
    setModalOpen(true);
  };
  // Open delete modal
  const handleDelete = (item) => {
    setSelectedItem(item);
    setModalType('delete');
    setModalOpen(true);
  };

  // Save edit
  const handleSaveEdit = () => {
    let updated;
    if (activeSection === 'contacts') {
      updated = contacts.map(c => (c === selectedItem ? editData : c));
      setContacts(updated);
      localStorage.setItem('contactSubmissions', JSON.stringify(updated));
    } else if (activeSection === 'appointments') {
      updated = appointments.map(a => (a === selectedItem ? editData : a));
      setAppointments(updated);
      localStorage.setItem('appointments', JSON.stringify(updated));
    } else if (activeSection === 'users') {
      updated = users.map(u => (u === selectedItem ? editData : u));
      setUsers(updated);
      localStorage.setItem('users', JSON.stringify(updated));
    }
    setModalOpen(false);
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    let updated;
    if (activeSection === 'contacts') {
      updated = contacts.filter(c => c !== selectedItem);
      setContacts(updated);
      localStorage.setItem('contactSubmissions', JSON.stringify(updated));
    } else if (activeSection === 'appointments') {
      updated = appointments.filter(a => a !== selectedItem);
      setAppointments(updated);
      localStorage.setItem('appointments', JSON.stringify(updated));
    } else if (activeSection === 'users') {
      updated = users.filter(u => u !== selectedItem);
      setUsers(updated);
      localStorage.setItem('users', JSON.stringify(updated));
    }
    setModalOpen(false);
  };

  // Table columns
  const getColumns = () => {
    if (activeSection === 'contacts') {
      return ['Name', 'Email', 'Message', 'Date'];
    } else if (activeSection === 'appointments') {
      return ['Name', 'Email', 'Date', 'Status'];
    } else if (activeSection === 'users') {
      return ['Name', 'Email', 'Role', 'Status'];
    }
    return [];
  };

  // Table rows
  const getRows = () => {
    const data = getFilteredData();
    if (activeSection === 'contacts') {
      return data.map((c, i) => [c.name, c.email, c.message, c.date, c]);
    } else if (activeSection === 'appointments') {
      return data.map((a, i) => [a.name, a.email, a.date, a.status, a]);
    } else if (activeSection === 'users') {
      return data.map((u, i) => [u.name, u.email, u.role, u.status, u]);
    }
    return [];
  };

  // Edit form fields
  const renderEditFields = () => {
    if (!editData) return null;
    if (activeSection === 'contacts') {
      return (
        <>
          <label className="block mb-2">Name<input className="w-full mb-2 p-2 border rounded" value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })} /></label>
          <label className="block mb-2">Email<input className="w-full mb-2 p-2 border rounded" value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} /></label>
          <label className="block mb-2">Message<textarea className="w-full mb-2 p-2 border rounded" value={editData.message || ''} onChange={e => setEditData({ ...editData, message: e.target.value })} /></label>
          <label className="block mb-2">Date<input className="w-full mb-2 p-2 border rounded" value={editData.date || ''} onChange={e => setEditData({ ...editData, date: e.target.value })} /></label>
        </>
      );
    } else if (activeSection === 'appointments') {
      return (
        <>
          <label className="block mb-2">Name<input className="w-full mb-2 p-2 border rounded" value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })} /></label>
          <label className="block mb-2">Email<input className="w-full mb-2 p-2 border rounded" value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} /></label>
          <label className="block mb-2">Date<input className="w-full mb-2 p-2 border rounded" value={editData.date || ''} onChange={e => setEditData({ ...editData, date: e.target.value })} /></label>
          <label className="block mb-2">Status<input className="w-full mb-2 p-2 border rounded" value={editData.status || ''} onChange={e => setEditData({ ...editData, status: e.target.value })} /></label>
        </>
      );
    } else if (activeSection === 'users') {
      return (
        <>
          <label className="block mb-2">Name<input className="w-full mb-2 p-2 border rounded" value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })} /></label>
          <label className="block mb-2">Email<input className="w-full mb-2 p-2 border rounded" value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} /></label>
          <label className="block mb-2">Role<input className="w-full mb-2 p-2 border rounded" value={editData.role || ''} onChange={e => setEditData({ ...editData, role: e.target.value })} /></label>
          <label className="block mb-2">Status<input className="w-full mb-2 p-2 border rounded" value={editData.status || ''} onChange={e => setEditData({ ...editData, status: e.target.value })} /></label>
        </>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-lg flex flex-col">
        <div className="p-6 text-2xl font-bold text-purple-700 border-b">Admin</div>
        <nav className="flex-1 p-4 space-y-2">
          {sections.map(sec => (
            <button
              key={sec.key}
              className={`flex items-center w-full px-4 py-2 rounded-lg text-lg transition font-semibold space-x-2 ${activeSection === sec.key ? 'bg-purple-100 text-purple-700' : 'hover:bg-purple-50 text-gray-700'}`}
              onClick={() => setActiveSection(sec.key)}
            >
              {sec.icon}
              <span>{sec.label}</span>
            </button>
          ))}
        </nav>
        <button className="flex items-center px-4 py-3 m-4 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition font-semibold space-x-2">
          <FaSignOutAlt /> <span>Logout</span>
        </button>
      </aside>
      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <h1 className="text-2xl font-bold capitalize">{activeSection}</h1>
          <div className="flex gap-2">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeSection}...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
            </div>
            <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">
              <FaFileCsv /> Export CSV
            </button>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {getColumns().map(col => (
                  <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col}</th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getRows().map((row, idx) => (
                <tr key={idx}>
                  {row.slice(0, -1).map((cell, i) => (
                    <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{cell}</td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap flex gap-2">
                    <button onClick={() => handleEdit(row[row.length-1])} className="text-blue-600 hover:text-blue-800"><FaEdit /></button>
                    <button onClick={() => handleDelete(row[row.length-1])} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                  </td>
                </tr>
              ))}
              {getRows().length === 0 && (
                <tr>
                  <td colSpan={getColumns().length + 1} className="px-6 py-8 text-center text-gray-400">No data found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Modals */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md relative">
              <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl" onClick={() => setModalOpen(false)}>&times;</button>
              {modalType === 'edit' ? (
                <>
                  <h2 className="text-xl font-bold mb-4">Edit {activeSection.slice(0, -1)}</h2>
                  <form onSubmit={e => { e.preventDefault(); handleSaveEdit(); }}>
                    {renderEditFields()}
                    <div className="flex justify-end gap-2 mt-4">
                      <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={() => setModalOpen(false)}>Cancel</button>
                      <button type="submit" className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700">Save</button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                  <p>Are you sure you want to delete this {activeSection.slice(0, -1)}?</p>
                  <div className="flex justify-end gap-2 mt-6">
                    <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={() => setModalOpen(false)}>Cancel</button>
                    <button className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700" onClick={handleConfirmDelete}>Delete</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard; 